import React, { ReactNode, useRef } from 'react';
import Wrapper from './styles';
import { FieldError, RegisterOptions, useFormContext } from 'react-hook-form';
import UiAlert from '../../../ui/alert/UiAlert';
import { translate } from '../../../../services/translate';
import UiFormLabel from '../../../ui/form/label/UiFormLabel';
import classNames from 'classnames';

interface Props extends Omit<React.HTMLProps<HTMLInputElement>, 'pattern'> {
    error?: FieldError;
    name: string;
    label?: string;
    type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'search';
    pattern?: RegExp;
    validator?: {
        validate: Record<string, (value: any, form?: any) => true | string | Promise<true | string>>;
    };
    dataTest?: string;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
}
export default function Ui2FormTextInput({
    name,
    error,
    label,
    dataTest,
    minLength,
    maxLength,
    pattern,
    validator,
    required = true,
    type = 'text',
    startAdornment,
    endAdornment,
    className,
    ...args
}: Props) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { register } = useFormContext();

    const options: RegisterOptions = {
        required: { value: required, message: translate('Oops! You forgot this field', 'ui.registration') },
        validate: validator?.validate,
    };

    if (minLength) {
        options.minLength = {
            value: minLength,
            message: translate(`Oops! Minimum required field length is {{ minLength }} characters.`, 'ui.common', {
                minLength,
            }),
        };
    }

    if (maxLength) {
        options.maxLength = {
            value: maxLength,
            message: translate(`Oops! Maximum allowed field length is {{ maxLength }} characters.`, 'ui.common', {
                maxLength,
            }),
        };
    }

    if (pattern) {
        options.pattern = {
            value: pattern,
            message: translate('Oops! You are using invalid characters.', 'ui.common'),
        };
    }

    const { ref, ...rest } = register(name, options);

    function renderInput() {
        return (
            <input
                {...rest}
                ref={(element) => {
                    ref(element);
                    inputRef.current = element;
                }}
                type={type}
                {...args}
                data-test={dataTest}
                className={classNames({ error })}
            />
        );
    }

    return (
        <Wrapper className={className}>
            {label && <UiFormLabel>{label}</UiFormLabel>}
            {startAdornment || endAdornment ? (
                <div className="input-wrapper">
                    {startAdornment && (
                        <div className="input-adornment" onClick={() => (inputRef.current as any).focus()}>
                            {startAdornment}
                        </div>
                    )}
                    {renderInput()}
                    {endAdornment && (
                        <div className="input-adornment" onClick={() => (inputRef.current as any).focus()}>
                            {endAdornment}
                        </div>
                    )}
                </div>
            ) : (
                renderInput()
            )}
            {error && (
                <UiAlert className="input-error" failure>
                    {error.message}
                </UiAlert>
            )}
        </Wrapper>
    );
}
